import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  clearStoredAuthSession,
  getConsentStatus,
  getCurrentUser,
  updateConsentStatus,
} from '@/lib/api/auth';
import { getDefaultAuthenticatedRoute } from '@/lib/accessControl';
import {
  clearConsentGateState,
  persistConsentGateState,
  readConsentGateState,
} from '@/lib/consentGate';
import { trackLogoutCompleted } from '@/lib/analytics/trackers/auth';
import { ConsentGateReason } from '@/types/auth';
import { showErrorToast, showSuccessToast, showWarningToast } from '@/utils/toast-utils';

const CONSENT_POLICY_VERSION = import.meta.env.VITE_CONSENT_POLICY_VERSION?.trim() || undefined;

const getReasonDescription = (reason: ConsentGateReason): string => {
  if (reason === 'revoked') {
    return 'Your prior consent was revoked. Please accept again to continue using protected features.';
  }
  return 'Before accessing protected data, please accept both Terms and Privacy consent.';
};

const ConsentRequiredPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [reason, setReason] = useState<ConsentGateReason>(
    readConsentGateState()?.reason ?? null
  );

  const returnTo = useMemo(() => {
    const fromQuery = searchParams.get('from')?.trim() || '';
    if (fromQuery.startsWith('/')) {
      return fromQuery;
    }

    const fromState = readConsentGateState()?.returnTo;
    if (typeof fromState === 'string' && fromState.startsWith('/')) {
      return fromState;
    }

    return getDefaultAuthenticatedRoute(getCurrentUser());
  }, [searchParams]);

  useEffect(() => {
    const currentUser = getCurrentUser();
    if (!currentUser?.token) {
      clearConsentGateState();
      navigate('/login', { replace: true });
      return;
    }

    let isCancelled = false;

    const loadStatus = async () => {
      setIsCheckingStatus(true);
      try {
        const response = await getConsentStatus();
        if (isCancelled) {
          return;
        }

        setReason(response.consent.reason ?? null);
        setTermsAccepted(Boolean(response.consent.termsAcceptedAt));
        setPrivacyAccepted(Boolean(response.consent.privacyAcceptedAt));

        if (!response.consent.accessBlocked) {
          clearConsentGateState();
          navigate(returnTo, { replace: true });
          return;
        }

        persistConsentGateState({
          accessBlocked: true,
          reason: response.consent.reason ?? null,
          returnTo,
        });
      } catch (error) {
        showErrorToast(error, 'Could not load consent status');
      } finally {
        if (!isCancelled) {
          setIsCheckingStatus(false);
        }
      }
    };

    void loadStatus();

    return () => {
      isCancelled = true;
    };
  }, [navigate, returnTo]);

  const handleSubmit = async () => {
    if (!termsAccepted || !privacyAccepted) {
      showWarningToast(
        'Consent required',
        'Please accept both consent checkboxes to continue.'
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await updateConsentStatus({
        termsAccepted: true,
        privacyAccepted: true,
        ...(CONSENT_POLICY_VERSION ? { policyVersion: CONSENT_POLICY_VERSION } : {}),
      });

      if (response.consent.accessBlocked) {
        persistConsentGateState({
          accessBlocked: true,
          reason: response.consent.reason ?? null,
          returnTo,
        });
        showWarningToast(
          'Consent still required',
          'Your consent could not be finalized yet. Please try again.'
        );
        return;
      }

      clearConsentGateState();
      showSuccessToast('Consent saved', 'Thanks. Your account access is now restored.');
      navigate(returnTo, { replace: true });
    } catch (error) {
      showErrorToast(error, 'Could not save consent');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    trackLogoutCompleted();
    clearStoredAuthSession();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#e9edf1] p-6 sm:p-8">
      <div className="mx-auto mt-8 w-full max-w-[680px] rounded-2xl border border-slate-200 bg-white p-8 sm:mt-14 sm:p-10">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#0f7fa4]">Account Access</p>
        <h1 className="mt-3 text-3xl font-semibold leading-tight text-slate-800">Consent required to continue</h1>
        <p className="mt-3 text-sm leading-6 text-slate-600">
          {getReasonDescription(reason)}
        </p>

        <div className="mt-8 space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-5">
          <label className="flex items-start gap-3 text-sm leading-6 text-slate-700">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(event) => setTermsAccepted(event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-[#20AEDC] focus:ring-[#20AEDC]"
            />
            <span>I accept the Terms and Conditions for using Calorie Science.</span>
          </label>

          <label className="flex items-start gap-3 text-sm leading-6 text-slate-700">
            <input
              type="checkbox"
              checked={privacyAccepted}
              onChange={(event) => setPrivacyAccepted(event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-[#20AEDC] focus:ring-[#20AEDC]"
            />
            <span>I accept the Privacy Policy and consent to processing required for protected features.</span>
          </label>
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
          <Button
            type="button"
            disabled={isCheckingStatus || isSubmitting}
            onClick={handleSubmit}
            className="!h-11 !rounded-md !bg-[#20AEDC] !px-6 !text-base !font-semibold !text-white hover:!bg-[#1a99c0] disabled:!bg-[#a9d5e7]"
          >
            {isCheckingStatus ? 'Checking...' : isSubmitting ? 'Saving...' : 'Continue'}
          </Button>
          <button
            type="button"
            onClick={handleLogout}
            className="text-sm font-medium text-slate-600 underline"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentRequiredPage;
